import { formatBillingMaterialLine } from "@/features/timesheetFlow/timesheetBillingUtils";
import type { BillingQuestion } from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetMaterialRecord } from "@/features/timesheetFlow/timesheetFlowTypes";

export interface BillingAnswerRow {
  key: string;
  questionId: number;
  itemId: number | null;
  questionText: string;
  materialLine: string | null;
  createdAt?: string | null;
  createdBy?: string | null;
  isOpen: boolean;
  answerText: string | null;
  price: string | number | null;
  /** Preisfeld bei Material-Rückfragen anzeigen. */
  showPriceField: boolean;
  materialPositionId?: number | null;
  respondedAt?: string | null;
  /** z. B. „aus verknüpfter Rechnung Nr. RE-12“ */
  sourceLabel?: string | null;
}

export function billingQuestionItemIsOpen(item: {
  response_text?: string | null;
  price?: string | number | null;
}): boolean {
  if (String(item.response_text || "").trim()) return false;
  const price = item.price;
  if (price != null && String(price).trim() !== "") return false;
  return true;
}

export function billingQuestionIsOpen(q: BillingQuestion): boolean {
  const generalQuestion = String(q.question_text || "").trim();
  const generalAnswer = String(q.response_text || "").trim();
  if (generalQuestion && !generalAnswer) return true;

  const items = Array.isArray(q.items) ? q.items : [];
  if (items.length === 0) return !q.responded_at;
  return items.some((item) => billingQuestionItemIsOpen(item));
}

function materialLineForItem(
  item: NonNullable<BillingQuestion["items"]>[number],
  materials: TimesheetMaterialRecord[],
): string | null {
  const matId = item.material_position_id;
  if (matId != null) {
    const mat = materials.find((m) => m.id === matId);
    if (mat) return formatBillingMaterialLine(mat).line || null;
  }
  const fallback = formatBillingMaterialLine({
    text: item.material_text ?? undefined,
    menge: item.material_menge ?? undefined,
    einheit: item.material_einheit ?? undefined,
  } as TimesheetMaterialRecord).line;
  return fallback || null;
}

function rowMeta(q: BillingQuestion): { createdAt?: string | null; createdBy?: string | null } {
  const ext = q as BillingQuestion & {
    created_at?: string | null;
    created_by_display?: string | null;
    created_by_name?: string | null;
    created_by_login?: string | null;
  };
  return {
    createdAt: ext.created_at ?? null,
    createdBy: ext.created_by_display || ext.created_by_name || ext.created_by_login || null,
  };
}

/** Kanonische Rückfragen in einzelne Antwort-Zeilen (allgemein + Materialpositionen). */
export function flattenBillingQuestionsToAnswerRows(
  questions: BillingQuestion[],
  materials: TimesheetMaterialRecord[],
): BillingAnswerRow[] {
  const rows: BillingAnswerRow[] = [];

  for (const q of questions) {
    const meta = rowMeta(q);
    const generalQuestion = String(q.question_text || "").trim();
    const generalAnswer = String(q.response_text || "").trim();
    const generalRespondedAt =
      (q as BillingQuestion & { responded_at?: string | null }).responded_at ?? null;
    const items = Array.isArray(q.items) ? q.items : [];

    if (generalQuestion) {
      rows.push({
        key: `q-${q.id}-general`,
        questionId: q.id,
        itemId: null,
        questionText: generalQuestion,
        materialLine: null,
        ...meta,
        isOpen: !generalAnswer,
        answerText: generalAnswer || null,
        price: null,
        showPriceField: false,
        materialPositionId: null,
        respondedAt: generalRespondedAt,
      });
    }

    for (const item of items) {
      const materialLine = materialLineForItem(item, materials);
      const itemQuestion = String(item.question_text || "").trim() || generalQuestion;
      if (!itemQuestion && !materialLine) continue;

      const open = billingQuestionItemIsOpen(item);
      const itemRespondedAt =
        (item as { responded_at?: string | null }).responded_at ?? generalRespondedAt;
      rows.push({
        key: `q-${q.id}-item-${item.id}`,
        questionId: q.id,
        itemId: item.id,
        questionText: itemQuestion,
        materialLine,
        ...meta,
        isOpen: open,
        answerText: String(item.response_text || "").trim() || null,
        price: item.price ?? null,
        showPriceField: Boolean(item.material_position_id != null || materialLine),
        materialPositionId: item.material_position_id ?? null,
        respondedAt: itemRespondedAt,
      });
    }

    if (!generalQuestion && items.length === 0) {
      rows.push({
        key: `q-${q.id}-empty`,
        questionId: q.id,
        itemId: null,
        questionText: "Rückfrage",
        materialLine: null,
        ...meta,
        isOpen: billingQuestionIsOpen(q),
        answerText: generalAnswer || null,
        price: null,
        showPriceField: false,
        materialPositionId: null,
        respondedAt: generalRespondedAt,
      });
    }
  }

  return rows;
}

export function groupMaterialAnswerRows(rows: BillingAnswerRow[]): Array<{
  materialLine: string;
  rows: BillingAnswerRow[];
}> {
  const groups = new Map<string, BillingAnswerRow[]>();
  for (const row of rows) {
    if (!row.materialLine) continue;
    const list = groups.get(row.materialLine) ?? [];
    list.push(row);
    groups.set(row.materialLine, list);
  }
  return [...groups.entries()].map(([materialLine, groupRows]) => ({
    materialLine,
    rows: groupRows,
  }));
}
